// Rules shared by the registration forms and the server (decision 2026-09-28).

/** Password: at least 8 characters and at least one special character (not a letter, digit or space). */
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 200;

export type PasswordProblem = 'too_short' | 'too_long' | 'no_special';

export function passwordProblem(password: string): PasswordProblem | null {
  const length = [...password].length;
  if (length < PASSWORD_MIN) return 'too_short';
  if (length > PASSWORD_MAX) return 'too_long';
  if (!/[^\p{L}\p{N}\s]/u.test(password)) return 'no_special';
  return null;
}

/**
 * Invite codes: 8 characters, capital letters and digits without the easily confused
 * 0/O and 1/I (32 characters → 32^8 ≈ 1.1 × 10^12 combinations).
 */
export const INVITE_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const INVITE_CODE_LENGTH = 8;

/** Accepts lower case, spaces and dashes (e.g. "abcd-efgh"). */
export function normalizeInviteCode(input: string): string {
  return input.toUpperCase().replace(/[\s-]/g, '');
}

export function isInviteCodeFormat(code: string): boolean {
  return code.length === INVITE_CODE_LENGTH && [...code].every((c) => INVITE_CODE_ALPHABET.includes(c));
}

/** Join link validity and invite batch validity. */
export const JOIN_LINK_TTL_MS = 2 * 60 * 60 * 1000;
export const INVITE_BATCH_VALID_MONTHS = 3;
export const INVITE_BATCH_MAX_CODES = 200;
export const INVITE_BATCH_LABEL_MAX = 40;

/** Browser routes (hash) of the public registration pages. Hash fragments never reach server logs. */
export const JOIN_ROUTES = { link: 'liity', reset: 'salasana', code: 'kutsu', guest: 'vieras' } as const;

/**
 * Guest codes (decision 2026-09-29): a batch is valid one week, a code gives one visit of at
 * most 3 hours.
 */
export const GUEST_BATCH_VALID_DAYS = 7;
export const GUEST_VISIT_MS = 3 * 60 * 60 * 1000;

/** Guest names: "Vieras" + an animal ("Vieras Kettu"), a number is added if the name is taken. */
export const GUEST_NAME_PREFIX = 'Vieras';
export const GUEST_ANIMALS = [
  'Kettu', 'Karhu', 'Ilves', 'Susi', 'Pöllö', 'Orava', 'Siili', 'Majava', 'Hirvi', 'Jänis',
  'Kotka', 'Joutsen', 'Hylje', 'Saukko', 'Näätä', 'Peura', 'Tikka', 'Varis', 'Lokki', 'Kurki',
  'Hauki', 'Lohi', 'Ahven', 'Sammakko', 'Mäyrä', 'Kärppä', 'Harakka', 'Tiainen', 'Haukka', 'Poro',
] as const;
